import Blue from '~/core'
import { createConfig, updateCurrentValues } from './calculator/_config'
import { calculateSavings, debounce, setCurrentDate } from './calculator/_utilities'
import { initializeChart, updateChart, updateChartLabels } from './calculator/_chart'
import { updateRatesTable } from './calculator/_table'
import { registerCursorTracker, formatNumeral, unformatNumeral, DefaultNumeralDelimiter } from 'cleave-zen'

new Blue({
  name: 'Calculator',
  selector: '.calculator--hisa',
  setup(calculator) {
    const dataRatesEl = calculator.dataset.ratesEl
    const dataChartId = calculator.dataset.chartId

    let rateTable = []

    const initialInput = calculator.querySelector('#initial_investment')
    const monthlyInput = calculator.querySelector('#monthly_contributions')
    const alternateInput = calculator.querySelector('#alternate_rate')

    const ratesEl = calculator.querySelectorAll(`.${dataRatesEl}`)
    const chartCanvas = calculator.querySelector(`#${dataChartId}`)
    const tableEl = calculator.querySelector('.cd__table')

    const formatPercentage = (value) => (Math.round(value * 100) / 100).toFixed(2)

    const config = createConfig('hisa')
    config.elements = {
      rates: ratesEl,
      chart: chartCanvas,
      table: tableEl,
    }

    config.currentValues = {
      initial_investment: 25000,
      investment_length: 1,
      monthly_contributions: 0,
      alternate_rate: 0.0,
    }

    const updateRate = (rate) => {
      ;[].forEach.call(config.elements.rates, (el) => {
        if (el.dataset.rate === rate) {
          el.classList.add('current')
        } else {
          el.classList.remove('current')
        }
      })
    }

    const buildRateTable = () => {
      const rateTable = []

      ;[].forEach.call(config.elements.rates, (el) => {
        const threshold = el.dataset.threshold
        const rate = el.dataset.rate
        if (threshold && rate) {
          rateTable.push([threshold, rate])
        }
      })

      return rateTable
    }

    const getRate = (initialInvestment, rates) => {
      let finalRate

      for (let i = 0; i < rates.length; i++) {
        const [threshold, _] = rates[i].map(Number)
        if (initialInvestment < threshold) {
          finalRate = formatPercentage(i === 0 ? rates[0][1] : rates[i - 1][1])
          updateRate(finalRate)
          return finalRate
        }
      }

      finalRate = formatPercentage(rates[rates.length - 1][1])
      updateRate(finalRate)
      return finalRate
    }

    // Function to prepare updates based on changes in the investment scenario
    function prepareUpdate() {
      let alternateRateValue = 1.83

      const initialInvestment = config.currentValues.initial_investment // calculator.querySelector('#initial_investment').value
      const investmentLength = config.currentValues.investment_length
      const alternateRate = config.currentValues.alternate_rate // calculator.querySelector('#alternate_rate')

      const percentageFormat = /^\d{1,3}(\.\d{1,2})?$/
      if (percentageFormat.test(alternateRate)) {
        alternateRateValue = alternateRate
      }

      const rate = getRate(unformatNumeral(initialInvestment + ''), rateTable)

      // Sent to updateChart(config)
      const rates = {
        scu: parseFloat(rate),
        banks: parseFloat(alternateRateValue),
      }

      const monthlyContribution = isNaN(config.currentValues.monthly_contributions)
        ? 0
        : Math.abs(config.currentValues.monthly_contributions)
      calculateSavings(config, 'scu', rates.scu, monthlyContribution, investmentLength)
      calculateSavings(config, 'banks', rates.banks, monthlyContribution, investmentLength)

      updateChartLabels(config, config.currentValues.investment_length)
      updateChart(config, config.currentValues, config.currentSavings, rates)

      const totalContributions =
        config.currentValues.monthly_contributions * (12 * config.currentValues.investment_length)
      updateRatesTable(config, totalContributions)
      // updateRatesTable(config, )
    }

    const initInputs = () => {
      registerCursorTracker({
        input: initialInput,
        delimiter: DefaultNumeralDelimiter,
      })
      registerCursorTracker({
        input: monthlyInput,
        delimiter: DefaultNumeralDelimiter,
      })
      // registerCursorTracker({
      //   input: alternateInput,
      //   delimiter: '.',
      // })

      const dollarPrefix = {
        prefix: '$',
      }
      initialInput.value = formatNumeral('25000', dollarPrefix)
      monthlyInput.value = formatNumeral('0', dollarPrefix)
      alternateInput.value = formatNumeral(formatPercentage(alternateInput.dataset.default)) + '%'

      initialInput.addEventListener('input', (e) => {
        const value = formatNumeral(e.target.value, dollarPrefix)
        initialInput.value = value
      })

      monthlyInput.addEventListener('input', (e) => {
        const value = formatNumeral(e.target.value, dollarPrefix)
        monthlyInput.value = value
      })

      alternateInput.addEventListener('input', (e) => {
        const value = formatNumeral(e.target.value, { delimiter: '.' })
        alternateInput.value = value + '%'
      })

      calculator.querySelector('.calculator__calculate').addEventListener(
        'input',
        debounce(function (event) {
          if (event.target.classList.contains('cc-field__input')) {
            if (event.target.id === 'investment_length' && event.target.value < 1) {
              updateCurrentValues('hisa', 'investment_length', 1)
            } else {
              updateCurrentValues('hisa', event.target.id, parseFloat(unformatNumeral(event.target.value)))
            }
            prepareUpdate()
          }
        }, 500),
      )
    }

    const updateAllValues = () => {
      const investment_length = document.querySelector('#investment_length')
      const investmentValue = parseInt(investment_length.value) >= 1 ? investment_length.value : '1'

      updateCurrentValues('hisa', 'initial_investment', parseInt(unformatNumeral(initialInput.value)))
      updateCurrentValues('hisa', 'investment_length', parseInt(unformatNumeral(investmentValue)))
      updateCurrentValues('hisa', 'monthly_contributions', parseInt(unformatNumeral(monthlyInput.value)))
      updateCurrentValues('hisa', 'alternate_rate', parseInt(unformatNumeral(alternateInput.value)))
    }

    rateTable = buildRateTable()

    initInputs()
    setCurrentDate()
    initializeChart(config)
    updateAllValues()
    prepareUpdate()
  },
})

new Blue({
  name: 'Squiggly',
  selector: '#squiggles',
  setup(squiggles) {
    const lottie = squiggles.getLottie()
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            lottie.play()
            observer.unobserve(squiggles)
            return
          }
        })
      },
      {
        rootMargin: '0px 0px -20% 0px',
      },
    )

    observer.observe(squiggles)
  },
})
